/**
 * The main/backup routes (PLAN §8.4: "Manual switchover is always available,
 * per production or for a whole node"; D9's per-production "manual only").
 *
 * NODE ONLY (`routes.ts` spreads these into `ROUTES`; the cloud does not
 * implement them, and `@acp/handlers`' rundown API excludes them): a
 * switchover moves the ACTIVE role between the two nodes of a site, which is
 * the nodes' business — the state service serves them itself
 * (`apps/state-service/src/redundancy/routes.ts`). Either node of the pair
 * answers for a production: the one that is active hands over, the standby
 * asks the active to.
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import { productionPathSchema } from './productions.js';
import {
  redundancySettingsResponseSchema,
  switchoverAllResponseSchema,
  switchoverRequestSchema,
  switchoverResultSchema,
  updateRedundancyRequestSchema,
} from './redundancy.js';
import type { RouteDef } from './routes.js';

export const REDUNDANCY_ROUTES = {
  switchoverProduction: {
    id: 'switchoverProduction',
    method: 'POST',
    path: '/productions/{productionId}/switchover',
    tag: 'redundancy',
    summary: 'Switch a production over to the other node',
    description:
      'A clean handover of the ACTIVE role to the other node of the pair (PLAN §8.4): the active mutes (its commands in flight are reported as uncertain), hands its final engine state to the standby, and the standby promotes — seeding each device in D8’s order (the device’s own state, the replicated actual state, a resync that never re-fires one-shot layers) — with the term bumped. ' +
      'Target < 500 ms (PLAN §1.1). Either node answers. Asked of the node holding the claim while its own process is held as a standby (isolated from the peer and every witness), it takes the production on air there at the next term — an operator’s override of "never promote blind". `conflict` when there is no ready standby (the peer is down, or its standby is still syncing) or the production is not running; node only.',
    params: productionPathSchema,
    query: null,
    body: switchoverRequestSchema,
    response: switchoverResultSchema,
    successStatus: 200,
    permission: 'node.switchover',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
  switchoverAll: {
    id: 'switchoverAll',
    method: 'POST',
    path: '/nodes/switchover-all',
    tag: 'redundancy',
    summary: 'Move every production active on this node to the other node',
    description:
      'The "switch whole node" button (D9): a switchover of every production whose active process runs on the node answering, one after another — before an image upgrade (PLAN §8.5), or to drain the node. Productions that cannot be moved are listed with the reason; node only.',
    params: null,
    query: null,
    body: switchoverRequestSchema,
    response: switchoverAllResponseSchema,
    successStatus: 200,
    permission: 'node.switchover',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed'],
  },
  updateRedundancy: {
    id: 'updateRedundancy',
    method: 'PUT',
    path: '/productions/{productionId}/redundancy',
    tag: 'redundancy',
    summary: 'Set a production’s redundancy settings',
    description:
      '`manualOnly: true` turns automatic failover off for this production (D9): a silent active then raises an alarm and waits for an operator’s switchover. Replicated to the other node; node only.',
    params: productionPathSchema,
    query: null,
    body: updateRedundancyRequestSchema,
    response: redundancySettingsResponseSchema,
    successStatus: 200,
    permission: 'node.switchover',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found'],
  },
} as const satisfies Record<string, RouteDef>;

export type RedundancyRouteId = keyof typeof REDUNDANCY_ROUTES;

export function isRedundancyRouteId(id: string): id is RedundancyRouteId {
  return Object.hasOwn(REDUNDANCY_ROUTES, id);
}
